import { useEffect, useRef, useState } from 'react'
import { useClickAway } from 'react-use'
import QRCode from 'react-qr-code'
import { Dialog, DialogContent } from '@/common/ui/dialog'
import { Button } from '@/common/ui/button'
import { H2 } from '@/common/ui/h2'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/common/ui/input-otp'
import { NAVIGATION_COPY } from '@/locales/en'
import { useBffMfaRegistration } from '../useBffMfaRegistration'

const MFA_ISSUER = 'SIX Invest'
const OTP_LENGTH = 6

function buildOtpauthUri(email: string, secretCode: string): string {
    const label = encodeURIComponent(`${MFA_ISSUER}:${email}`)
    const issuer = encodeURIComponent(MFA_ISSUER)
    return `otpauth://totp/${label}?secret=${secretCode}&issuer=${issuer}`
}

export type MfaRegistrationModalViewStep = 'associate' | 'verify'

export interface MfaRegistrationModalViewProps {
    step: MfaRegistrationModalViewStep
    email: string
    secretCode: string | null
    isRunning: boolean
    onContinue: () => void
    onVerify: (code: string) => void
    onClose: () => void
}

export function MfaRegistrationModalView({
    step,
    email,
    secretCode,
    isRunning,
    onContinue,
    onVerify,
    onClose,
}: MfaRegistrationModalViewProps) {
    const [code, setCode] = useState('')
    const modalRef = useRef(null)
    useClickAway(modalRef, onClose)

    const handleOtpChange = (value: string) => {
        setCode(value.replace(/\D/g, ''))
    }

    const copy = NAVIGATION_COPY.account.mfaRegistrationModal

    return (
        <Dialog open>
            <div className="py-10 h-screen">
                <DialogContent
                    ref={modalRef}
                    onClose={onClose}
                    className="px-5 py-6 max-h-[90vh] w-[94vw] max-w-[680px] overflow-y-auto sm:p-20"
                >
                    {step === 'associate' ? (
                        <div className="flex flex-col items-center gap-6 min-w-0">
                            <H2 className="text-center">{copy.associateTitle}</H2>
                            <p className="text-center">{copy.associateInstructions}</p>
                            {secretCode && (
                                <>
                                    <div className="w-full max-w-[200px] sm:max-w-[256px]">
                                        <QRCode
                                            value={buildOtpauthUri(email, secretCode)}
                                            style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                                        />
                                    </div>
                                    <div className="text-center">
                                        <p className="text-sm text-muted-foreground">{copy.manualKeyLabel}</p>
                                        <p className="font-mono select-all">{secretCode}</p>
                                    </div>
                                </>
                            )}
                            <Button variant="secondary" disabled={!secretCode || isRunning} onClick={onContinue}>
                                {copy.continueButton}
                            </Button>
                        </div>
                    ) : (
                        <form
                            className="flex flex-col items-center gap-6 min-w-0"
                            onSubmit={(e) => {
                                e.preventDefault()
                                onVerify(code)
                            }}
                        >
                            <H2 className="text-center">{copy.verifyTitle}</H2>
                            <p className="text-center">{copy.verifyInstructions}</p>
                            <InputOTP
                                maxLength={OTP_LENGTH}
                                value={code}
                                onChange={handleOtpChange}
                                name="otp"
                                id="otp"
                                autoComplete="one-time-code"
                                aria-label="One-time verification code"
                            >
                                <InputOTPGroup>
                                    {Array.from({ length: OTP_LENGTH }).map((_, i) => (
                                        <InputOTPSlot
                                            key={i}
                                            index={i}
                                            className="w-8 h-12 mx-0.5 sm:w-10 sm:h-14 sm:mx-1"
                                        />
                                    ))}
                                </InputOTPGroup>
                            </InputOTP>
                            <Button
                                type="submit"
                                variant="secondary"
                                disabled={code.length !== OTP_LENGTH || isRunning}
                                loading={isRunning}
                            >
                                {copy.verifyButton}
                            </Button>
                        </form>
                    )}
                </DialogContent>
            </div>
        </Dialog>
    )
}

export interface MfaRegistrationModalProps {
    email: string
    onClose: () => void
    onEnabled: () => void
}

export function MfaRegistrationModal({ email, onClose, onEnabled }: MfaRegistrationModalProps) {
    const { step, secretCode, isRunning, startAssociation, verifyCode } = useBffMfaRegistration(onEnabled)
    const [readyForCode, setReadyForCode] = useState(false)
    const associationStarted = useRef(false)

    useEffect(() => {
        if (associationStarted.current) return
        associationStarted.current = true
        void startAssociation()
    }, [startAssociation])

    const viewStep: MfaRegistrationModalViewStep = step === 'verify' && readyForCode ? 'verify' : 'associate'

    return (
        <MfaRegistrationModalView
            step={viewStep}
            email={email}
            secretCode={secretCode}
            isRunning={isRunning}
            onContinue={() => setReadyForCode(true)}
            onVerify={verifyCode}
            onClose={onClose}
        />
    )
}
